import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { useCreateChatChannel } from "@src/appV2/Facilities/Chat/api/useCreateChatChannel";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { generatePath, useHistory, useLocation } from "react-router-dom";

import { ChatWithClipboardGuidesButton } from "../../ClipboardGuides/components/ChatWithClipboardGuidesButton";
import { useIsClipboardGuidesButtonVisible } from "../../ClipboardGuides/hooks/useIsClipboardGuidesButtonVisible";
import { Button } from "../../components/Button";
import { PlacementChatWithWorkplace } from "../../Placements/components/PlacementChatWithWorkplace";
import { type WorkplaceLocationState } from "../types";
import { isHomeHealthOrHospiceWorkplace } from "../utils";

interface WorkplaceProfileActionsProps {
  workplaceId: string;
  workplaceName: string;
  phoneNumber?: string;
  displayChatButton?: boolean;
  sourcePlacementId?: string;
  workplaceType?: string;
}

export function WorkplaceProfileActions(props: WorkplaceProfileActionsProps) {
  const {
    workplaceId,
    workplaceName,
    phoneNumber,
    displayChatButton = true,
    sourcePlacementId,
    workplaceType,
  } = props;
  const worker = useDefinedWorker();
  const history = useHistory();
  const location = useLocation<WorkplaceLocationState>();
  const shiftContext = location.state?.clipboardGuidesShiftContext;
  const isClipboardGuidesButtonVisible = useIsClipboardGuidesButtonVisible({ shiftContext });

  const { mutateAsync: createChatChannel, isLoading: isCreatingChatChannel } =
    useCreateChatChannel();

  return (
    <Stack direction="row" spacing={3}>
      {displayChatButton && !isDefined(sourcePlacementId) && (
        <Button
          invert
          startIconType="text-bubble"
          size="small"
          variant="contained"
          disabled={isCreatingChatChannel}
          sx={(theme) => ({ boxShadow: theme.shadows[2] })}
          onClick={async () => {
            await createChatChannel({
              facilityId: workplaceId,
              agentId: worker.userId,
            });

            history.push(
              generatePath(DeprecatedGlobalAppV1Paths.CHAT, {
                facilityId: workplaceId,
              })
            );
            logEvent(APP_V2_USER_EVENTS.CHAT_WITH_WORKPLACE, { source: "workplace-profile" });
          }}
        >
          {isHomeHealthOrHospiceWorkplace(workplaceType)
            ? "Chat with Agency"
            : "Chat with workplace"}
        </Button>
      )}
      {isClipboardGuidesButtonVisible && isDefined(shiftContext) && (
        <ChatWithClipboardGuidesButton
          workplaceId={workplaceId}
          workplaceName={workplaceName}
          shiftId={shiftContext.shiftId}
        />
      )}
      {isDefined(sourcePlacementId) && (
        <PlacementChatWithWorkplace placementId={sourcePlacementId} workplaceId={workplaceId} />
      )}
      {isDefined(phoneNumber) && !isHomeHealthOrHospiceWorkplace(workplaceType) && (
        <Button
          invert
          startIconType="phone"
          size="small"
          variant="contained"
          href={`tel:+1${phoneNumber}`}
          sx={(theme) => ({ boxShadow: theme.shadows[2] })}
          onClick={() => {
            logEvent(APP_V2_USER_EVENTS.CALL_WORKPLACE, { source: "workplace-profile" });
          }}
        >
          Call
        </Button>
      )}
    </Stack>
  );
}
